import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Phone,
  ChevronDown,
  X,
  Menu,
  Smartphone,
  Laptop,
  Monitor,
  HardDrive,
  Gamepad2,
  CheckCircle2,
  ShieldCheck,
  Zap,
  Truck,
  Star,
  ArrowRight
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { trackCallConversion } from '../../utils/tracking';
import './Navbar.css';


export default function Navbar() {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const location = useLocation();
  const isHome = location.pathname === '/';

  // Navbar visibility on scroll: Hidden at top (scrollY === 0), visible when scrolled down
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 30) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      {/* Main Navbar Header: Hidden at top (scrollY=0), appears smoothly when user scrolls */}
      <header className={`navbar-header ${isVisible ? 'navbar-visible' : ''}`}>

        {/* Announcement Ticker */}
        {/* <div className="announcement-bar">
          <div className="announcement-track">
            <span className="announcement-item">
              <Truck size={13} className="ann-icon" /> Free Doorstep Visit
            </span>
            <span className="ann-dot">•</span>
            <span className="announcement-item">
              <Star size={13} className="ann-icon" /> Genuine Parts
            </span>
            <span className="ann-dot">•</span>
            <span className="announcement-item">
              <ShieldCheck size={13} className="ann-icon" /> Up to 1 Year Warranty
            </span>
            <span className="ann-dot">•</span>
            <span className="announcement-item">
              <Zap size={13} className="ann-icon" /> Same Day Service
            </span>
          </div>
        </div> */}

        <div className="navbar-container">
          {/* Left: Brand Logo */}
          <Link to="/" className="navbar-logo">
            <img 
              src="/dmsr_logo_new.png" 
              alt="DSMR Solutions Logo" 
              className="navbar-logo-img" 
            />
            <div className="logo-text-group">
              <span className="logo-brand-name">
                DSMR <span className="logo-accent">Solutions</span>
              </span>
              <span className="logo-tagline">Doorstep Device Repair</span>
            </div>
          </Link>

          {/* Center: Desktop Navigation */}
          <nav className="desktop-nav" aria-label="Main Navigation">
            <Link to="/" className={`nav-link ${isHome ? 'active' : ''}`}>
              Home
            </Link>

            <Link to="/services" className={`nav-link ${location.pathname === '/services' ? 'active' : ''}`}>
              Services
            </Link>

            {/* <Link to="/electrician" className={`nav-link ${location.pathname === '/electrician' ? 'active' : ''}`}>
              Electrician
            </Link> */}

            {/* <Link to="/iphone-repair" className={`nav-link ${location.pathname === '/iphone-repair' ? 'active' : ''}`}>
              iPhone Repair
            </Link> */}

            <Link to="/data-recovery" className={`nav-link ${location.pathname === '/data-recovery' ? 'active' : ''}`}>
              Data Recovery
            </Link>

            <Link to="/about" className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`}>
              About Us
            </Link>

            <a href="/#booking" className="nav-link">
              Contact
            </a>
          </nav>

          {/* Right: Actions */}
          <div className="navbar-actions">
            {/* WhatsApp Link Icon */}
            <a
              href="https://wa.me/918700319769"
              target="_blank"
              rel="noopener noreferrer"
              className="action-icon-btn whatsapp-action-btn"
              aria-label="Chat on WhatsApp"
            >
              <FaWhatsapp size={19} />
            </a>

            {/* Quick Call Button */}
            <a
              href="tel:+918700319769"
              className="call-now-btn"
              data-dsmr-phone="true"
              onClick={(e) => trackCallConversion(e, "tel:+918700319769")}
            >
              <Phone size={15} />
              <span>Call Now</span>
            </a>

            {/* Primary CTA: Book Repair */}
            <a href="/#booking" className="primary-cta-btn">
              <span>Book Repair</span>
            </a>

            {/* Mobile Hamburger Toggle Button */}
            <button
              className={`hamburger-toggle ${isMobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle Navigation Menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Top-to-Bottom Vertical Dropdown Menu */}
        <div className={`mobile-dropdown-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-dropdown-content">
            <Link to="/" className="mobile-nav-link" onClick={toggleMobileMenu}>
              Home
            </Link>

            <Link to="/electrician" className="mobile-nav-link" onClick={toggleMobileMenu}>
              Electrician & Contracting
            </Link>

            <Link to="/iphone-repair" className="mobile-nav-link" onClick={toggleMobileMenu}>
              iPhone Repair
            </Link>

            <Link to="/macbook-repair" className="mobile-nav-link" onClick={toggleMobileMenu}>
              MacBook Repair
            </Link>

            <Link to="/data-recovery" className="mobile-nav-link" onClick={toggleMobileMenu}>
              Data Recovery
            </Link>

            <Link to="/services" className="mobile-nav-link" onClick={toggleMobileMenu}>
              Services
            </Link>

            <Link to="/about" className="mobile-nav-link" onClick={toggleMobileMenu}>
              About Us
            </Link>

            <a href="/#booking" className="mobile-nav-link" onClick={toggleMobileMenu}>
              Contact
            </a>

            {/* Mobile Actions Bar */}
            <div className="mobile-bottom-actions">
              <a
                href="tel:+918700319769"
                className="mobile-call-btn"
                data-dsmr-phone="true"
                onClick={(e) => trackCallConversion(e, "tel:+918700319769")}
              >
                <Phone size={16} />
                <span>Call Now</span>
              </a>

              <a href="/#booking" className="mobile-book-btn" onClick={toggleMobileMenu}>
                <Zap size={16} />
                <span>Book Repair</span>
              </a>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
